/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  AxiosResponseHeaders,
  InternalAxiosRequestConfig,
  RawAxiosResponseHeaders
} from "axios";
import { getURL } from "@utils/Common";
import { HttpStatus } from "@configs/HttpStatus";
import { REDIRECT_PARAM } from "@resources/Constants";
import { isKeycloakEnabled } from "@configs/Env";
import { keycloak } from "@auth/keycloak";
import { attachAuthHeaders } from "@auth/attachAuthHeaders";
import {
  refreshAuthToken,
  redirectToPlatformLogin,
  setAccessToken
} from "@auth/tokenManager";

interface CustomAxiosResponse<T> {
  data: T;
  response?: T;
}

export interface CustomAxiosInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<
      AxiosResponse<CustomAxiosResponse<any>, any>
    >;
  };

  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

export const BASE_URL = `https://api.${getURL()}`;
let isRefreshing = false;
let refreshSubscribers: (() => void)[] = [];

const instance: CustomAxiosInstance = axios.create({
  baseURL: BASE_URL,
  // Bearer + SSO iframe 안정성을 위해 쿠키도 병행 전송(wind-blade 정렬).
  withCredentials: true
});

instance.interceptors.request.use(
  async (config) => {
    const newConfig = { ...config };
    newConfig.headers = {
      ...newConfig.headers
    } as AxiosRequestHeaders;
    // Keycloak 활성 시 Bearer 토큰 + 커스텀 헤더 주입 (비활성 시 그대로 반환).
    return attachAuthHeaders(newConfig);
  },
  (error) => {
    return Promise.reject(error);
  }
);

const isJSONResponse = (
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders
) => {
  return (
    headers["content-type"] &&
    headers["content-type"].includes("application/json")
  );
};

const isForbiddenUser = (errorResponseData: {
  message: string;
  error: string;
  statusCode: number;
}) => {
  return errorResponseData.message === "Forbidden resource";
};

instance.interceptors.response.use(
  (response) => {
    if (isJSONResponse(response.headers)) {
      return { ...response, data: response.data.data };
    }
    return { ...response, data: response.data };
  },
  async (error) => {
    if (error.response) {
      console.error(error.response.data);
      if (error.response.status === HttpStatus.UNAUTHORIZED) {
        const originalRequest = error.config;

        // Keycloak Bearer: 401 → 토큰 갱신 후 재시도. 실패 시 Platform(재로그인)으로 이동.
        if (isKeycloakEnabled()) {
          if (originalRequest && !originalRequest.retry) {
            originalRequest.retry = true;
            try {
              const token = await refreshAuthToken(-1);
              if (token && keycloak.authenticated) {
                originalRequest.headers = {
                  ...originalRequest.headers,
                  Authorization: `Bearer ${token}`
                };
                return instance(originalRequest);
              }
            } catch {
              setAccessToken(null);
            }
          }
          redirectToPlatformLogin({ bypassGuard: true });
          return Promise.reject(error);
        }

        // 비-Keycloak(로컬 개발) 폴백: 기존 서버 쿠키 refresh 흐름 유지.
        if (error.response?.data?.code === 401000) {
          redirectToPlatformLogin({ bypassGuard: true });
        }

        if (error.response?.data?.code === 401001 && !originalRequest.retry) {
          originalRequest.retry = true;

          if (!isRefreshing) {
            isRefreshing = true;
            instance
              .post(`/v1/auth/access-token/refresh`)
              .then(() => {
                isRefreshing = false;
                refreshSubscribers.forEach((callback) => {
                  callback();
                });
                refreshSubscribers = [];
              })
              .catch(() => {
                isRefreshing = false;

                redirectToPlatformLogin({ bypassGuard: true });
              });
          }

          return new Promise((res) => {
            refreshSubscribers.push(() => res(axios(originalRequest)));
          });
        }
      }
      if (
        error.response.status === HttpStatus.FORBIDDEN &&
        isForbiddenUser(error.response.data)
      ) {
        if (!/\/forbidden/.test(window.location.href)) {
          const currentUrl = window.location.href;
          window.location.href = `/forbidden?${REDIRECT_PARAM}=${currentUrl}`;
        }
      }
    }

    return Promise.reject(error);
  }
);

export const Get = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  const { data } = await instance.get<AxiosResponse>(url, config);
  return data;
};

export const Post = async <T>(
  url: string,
  req?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  const { data } = await instance.post<AxiosResponse>(url, req, config);
  return data;
};
export const PostWithStatusReturn = async (
  url: string,
  req?: any,
  config?: AxiosRequestConfig
): Promise<number> => {
  const { status } = await instance.post<AxiosResponse>(url, req, config);
  return status;
};

export const Put = async <T>(
  url: string,
  req?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  const { data } = await instance.put<AxiosResponse>(url, req, config);
  return data;
};

export const Patch = async <T>(
  url: string,
  req?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  const { data } = await instance.patch<AxiosResponse>(url, req, config);
  return data;
};

export const Delete = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  const { data } = await instance.delete<AxiosResponse>(url, config);
  return data;
};

export const GetCSV = (
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<Blob>> => {
  return instance.request<Blob>({
    url,
    method: "GET",
    responseType: "blob",
    ...config
  });
};
